import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, Award, Users, FileText, Hammer, Star, Phone, Mail, Clock, UserCheck } from "lucide-react";
import SEO from "@/components/SEO";
import { SectionHead, Reveal, PHONE, PHONE_LINK, EMAIL } from "@/components/Ui";
import FAQAccordion from "@/components/FAQAccordion";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import DevisForm from "@/components/DevisForm";

const prestations = [
  { t: "Maçonnerie générale", d: "Murs, parpaings, briques, ouvrages en béton.", to: "/maconnerie-bernay" },
  { t: "Rénovation", d: "Réparation de murs, fissures, rejointoiement.", to: "/renovation-bernay" },
  { t: "Gros œuvre", d: "Structures porteuses et bases de votre construction.", to: "/gros-oeuvre" },
  { t: "Construction de murs", d: "Murs porteurs, murets et façades.", to: "/construction-murs" },
  { t: "Fondations", d: "Semelles et fondations adaptées au terrain.", to: "/fondations" },
  { t: "Dalles et béton", d: "Dalles, chapes, béton lissé ou désactivé.", to: "/dalles-beton" },
  { t: "Parement brique", d: "Habillage de façades, pignons et murs intérieurs.", to: "/parement-brique" },
  { t: "Extensions", d: "Agrandissement de maison, garage, carport.", to: "/extension-maison" },
  { t: "Ouvertures de murs", d: "Portes, fenêtres, baies vitrées.", to: "/ouverture-mur-porteur" },
  { t: "Terrasses", d: "Terrasses béton, pavées ou carrelées.", to: "/beton-amenagements-exterieurs" },
  { t: "Aménagements extérieurs", d: "Allées, bordures, clôtures, escaliers.", to: "/beton-amenagements-exterieurs" },
  { t: "Rejointoiement", d: "Réparation et reprise de maçonnerie ancienne.", to: "/rejointoiement" },
];

const atouts = [
  { icon: FileText, t: "Devis gratuit et clair", d: "Une réponse détaillée et compréhensible avant tout engagement." },
  { icon: Award, t: "Travail soigné", d: "Des ouvrages réalisés avec attention et dans le respect des exigences du chantier." },
  { icon: Users, t: "Accompagnement personnalisé", d: "Un échange direct pour comprendre le projet et proposer une solution adaptée." },
  { icon: MapPin, t: "Intervention locale", d: "Une entreprise basée près de Bernay, intervenant dans l'Eure et selon les projets en Normandie." },
  { icon: UserCheck, t: "Un interlocuteur unique", d: "Un suivi simple et direct du projet, du premier échange à la fin du chantier." },
];

const realisations = [
  { img: "/realisations/terrasse-maison-beton.webp", alt: "Terrasse en béton lissé réalisée par DMZ Normandie dans l'Eure", t: "Terrasse béton & maison" },
  { img: "/realisations/pignons-parpaing.webp", alt: "Élévation de murs en parpaings avec pignons, maçonnerie DMZ Normandie", t: "Élévation parpaings & pignons" },
  { img: "/realisations/mur-cloture-portail.webp", alt: "Mur de clôture et pilier de portail maçonnés par DMZ Normandie", t: "Mur de clôture & portail" },
  { img: "/realisations/terrasse-carrelee-bernay.webp", alt: "Terrasse carrelée réalisée à Bernay par DMZ Normandie", t: "Terrasse carrelée — Bernay" },
  { img: "/realisations/renovation-facade-bernay.webp", alt: "Rénovation de façade en brique à Bernay par DMZ Normandie", t: "Façade rénovée — Bernay" },
  { img: "/realisations/vide-sanitaire-parpaing.webp", alt: "Vide sanitaire et soubassements en parpaings au coucher du soleil, par DMZ Normandie", t: "Vide sanitaire & soubassements" },
  { img: "/realisations/facade-brique-silex.webp", alt: "Rénovation d'une façade en brique et silex par DMZ Normandie", t: "Façade brique & silex" },
  { img: "/realisations/garage-carport.webp", alt: "Extension : garage et carport construits par DMZ Normandie", t: "Garage & carport" },
];

const faqItems = [
  { q: "Quels travaux de maçonnerie réalise DMZ Normandie ?", a: "Maçonnerie générale, gros œuvre, construction de murs, fondations, dalles et chapes béton, extensions, ouvertures de murs porteurs, murets, rénovation et rejointoiement — pour les particuliers et les professionnels." },
  { q: "Dans quelles villes intervenez-vous ?", a: "Notre secteur principal est Bernay, Évreux et l'Eure. Selon la nature et l'importance du chantier, nous intervenons aussi dans d'autres secteurs de Normandie." },
  { q: "Intervenez-vous à Évreux ?", a: "Oui, Évreux fait partie de notre zone d'intervention principale, comme Bernay et l'ensemble de l'Eure." },
  { q: "Intervenez-vous à Bernay ?", a: "Oui, Bernay et ses communes alentours sont au cœur de notre activité : c'est notre secteur principal." },
  { q: "Faites-vous des extensions de maison ?", a: "Oui : étude du projet, fondations, dalle, élévation des murs et ouvertures. Nous prenons en charge tout le gros œuvre de votre extension ou agrandissement." },
  { q: "Réalisez-vous les fondations ?", a: "Oui, nous préparons et coulons les semelles et fondations adaptées à votre terrain, pour les constructions neuves comme pour les extensions." },
  { q: "Réalisez-vous les dalles béton ?", a: "Oui : dalles, chapes, béton lissé ou désactivé, pour l'intérieur comme pour l'extérieur — terrasses, garages, allées." },
  { q: "Pouvez-vous réaliser une ouverture dans un mur porteur ?", a: "Oui, après analyse de la configuration existante. Les solutions de renforcement (linteau, poutre, jambages) sont étudiées selon les contraintes techniques de votre chantier." },
  { q: "Faites-vous de la rénovation de maçonnerie ?", a: "Oui : réparation de murs, traitement des fissures, rejointoiement brique, pierre et silex, rénovation intérieure et extérieure." },
  { q: "Comment demander un devis ?", a: "Appelez-nous au 07 45 04 91 89 ou remplissez le formulaire de devis gratuit : décrivez votre projet et nous vous recontactons pour échanger sur vos besoins." },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": ["GeneralContractor", "ProfessionalService"],
    name: "DMZ Normandie",
    description: "Entreprise de maçonnerie et rénovation à Bernay, dans l'Eure (27) et en Normandie : maçonnerie générale, gros œuvre, extensions, fondations, murs et ouvrages en béton.",
    telephone: "+33745049189",
    email: "dmznormandie@gmail.com",
    url: "https://dmznormandie.fr",
    address: { "@type": "PostalAddress", streetAddress: "7 rue Jean de la Fontaine", postalCode: "27000", addressLocality: "Évreux", addressRegion: "Normandie", addressCountry: "FR" },
    areaServed: ["Bernay", "Évreux", "Beuzeville", "Fontaine-l'Abbé", "Menneval", "Serquigny", "Beaumont-le-Roger", "Brionne", "Le Neubourg", "Louviers", "Vernon", "Rouen", "Eure", "Normandie"],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
];

export default function Home() {
  return (
    <>
      <SEO
        title="Maçonnerie à Bernay & dans l'Eure | DMZ Normandie"
        description="DMZ Normandie, entreprise de maçonnerie et rénovation à Bernay. Maçonnerie générale, extensions, fondations, béton, murs et rénovation dans l'Eure et en Normandie."
        path="/"
        jsonLd={jsonLd}
      />

      <Hero />
      <Marquee />

      <section className="py-20 lg:py-28" data-testid="presentation-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <SectionHead overline="Qui sommes-nous" title="Votre entreprise de maçonnerie en Normandie" />
            <div className="-mt-6 space-y-5 text-zinc-400 font-light leading-relaxed">
              <p>
                DMZ Normandie accompagne les particuliers et les professionnels dans leurs projets de
                maçonnerie, de construction et de rénovation. Basée près de Bernay, l'entreprise intervient
                dans l'Eure et selon les projets dans toute la Normandie.
              </p>
              <p>
                De la construction de murs aux fondations, des dalles béton aux extensions de maison,
                chaque chantier est étudié selon les contraintes du bâtiment, du terrain et les attentes du client.
              </p>
              <p>
                Notre objectif : proposer un travail sérieux, soigné et durable, avec un accompagnement
                clair à chaque étape du projet.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border border-[#C4A484]" aria-hidden="true" />
              <img
                src="/realisations/pignons-parpaing.webp"
                alt="Travaux de maçonnerie à Bernay : élévation de murs en parpaings par DMZ Normandie"
                className="relative w-full h-[400px] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0D0D0D] border-y border-white/10 py-20 lg:py-28" data-testid="realisations-preview">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <SectionHead overline="Nos chantiers" title="Nos réalisations" />
            <Link to="/realisations" className="btn-outline-light shrink-0 mb-4" data-testid="realisations-link">
              Toutes nos réalisations <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {realisations.map((r, i) => (
              <Reveal key={r.t} delay={(i % 3) * 0.08} className="group">
                <div className="overflow-hidden relative">
                  <img src={r.img} alt={r.alt} loading="lazy" className="w-full h-64 object-cover scale-[1.06] group-hover:scale-100 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/0 transition-colors duration-700" />
                </div>
                <p className="mt-3 font-serif-display font-light text-lg text-[#F5F5F0]">{r.t}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="prestations-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHead overline="Ce que nous faisons" title="Nos prestations" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {prestations.map((p, i) => (
              <Reveal key={p.t} delay={(i % 5) * 0.05}>
                <Link to={p.to} data-testid={`prestation-${i}`} className="group block card-dark p-6 h-full">
                  <Hammer size={18} className="text-[#C4A484] mb-4" />
                  <h3 className="font-medium text-[#F5F5F0] mb-2 group-hover:text-[#C4A484] transition-colors">{p.t}</h3>
                  <p className="text-sm font-light text-zinc-500 leading-relaxed">{p.d}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0D0D0D] border-y border-white/10 py-20 lg:py-28" data-testid="pourquoi-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHead overline="Nos engagements" title="Pourquoi choisir DMZ Normandie ?" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {atouts.map((a, i) => (
              <Reveal key={a.t} delay={i * 0.06} className="card-dark p-8">
                <a.icon size={22} className="text-[#C4A484] mb-5" />
                <h3 className="font-medium text-[#F5F5F0] mb-3">{a.t}</h3>
                <p className="text-sm font-light text-zinc-500 leading-relaxed">{a.d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-8 border-t border-white/10 pt-12" data-testid="trust-band">
            {[
              { v: "10 ans", l: "d'expérience" },
              { v: "150", l: "chantiers réalisés" },
              { v: "24h/24", l: "joignable 7j/7" },
              { v: "Eure 27", l: "et toute la Normandie" },
            ].map((s) => (
              <div key={s.l} className="text-center">
                <p className="font-serif-display font-light text-3xl md:text-4xl text-[#C4A484]">{s.v}</p>
                <p className="mt-2 text-xs tracking-[0.15em] uppercase text-zinc-500">{s.l}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="zone-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <SectionHead overline="Zone d'intervention" title="Nous intervenons dans l'Eure et en Normandie" center />
          <Reveal className="max-w-3xl mx-auto">
            <p className="text-zinc-400 font-light leading-relaxed">
              Basée près de Bernay, DMZ Normandie intervient principalement à Bernay, Évreux et dans l'Eure.
              Selon la nature et l'importance du chantier, nous pouvons également intervenir dans d'autres
              secteurs de Normandie.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {["Bernay", "Évreux", "Eure", "Normandie"].map((z) => (
              <div key={z} className="card-dark py-10" data-testid={`zone-${z.toLowerCase()}`}>
                <p className="font-serif-display font-light text-2xl md:text-3xl tracking-wide text-[#F5F5F0] uppercase">{z}</p>
              </div>
            ))}
          </Reveal>
          <Reveal delay={0.15} className="mt-10 max-w-3xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2" data-testid="zone-communes">
              {["Bernay", "Évreux", "Beuzeville", "Pont-Audemer", "Thiberville", "Deauville", "Fontaine-l'Abbé", "Menneval", "Serquigny", "Beaumont-le-Roger", "Brionne", "Le Neubourg", "Louviers", "Vernon", "Rouen"].map((c) => (
                <span key={c} className="border border-white/10 px-4 py-2 text-sm font-light text-zinc-400">{c}</span>
              ))}
            </div>
            <Link to="/zones" className="inline-block mt-8 text-xs tracking-[0.25em] uppercase text-[#C4A484] hover:text-[#F5F5F0] transition-colors" data-testid="zone-link">
              Voir le détail de nos zones d'intervention →
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0D0D0D] border-y border-white/10 py-20 lg:py-28" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <SectionHead overline="Questions fréquentes" title="Vos questions, nos réponses" />
          <FAQAccordion items={faqItems} testid="home-faq" />
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="avis-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHead overline="Ils nous font confiance" title="Avis clients" center />
          <Reveal className="text-center mb-14">
            <div className="inline-flex items-center gap-4 card-dark px-8 py-5" data-testid="avis-score">
              <span className="font-serif-display font-light text-5xl text-[#C4A484]">5,0</span>
              <span className="flex flex-col items-start gap-1">
                <span className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="text-[#C4A484] fill-[#C4A484]" />
                  ))}
                </span>
                <span className="text-xs tracking-[0.15em] uppercase text-zinc-500">12 avis Google</span>
              </span>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { name: "Vincent B.", since: "Il y a 4 semaines" },
              { name: "Sandrine L.", since: "Il y a 15 semaines" },
              { name: "Figen Y.", since: "Il y a 17 semaines" },
              { name: "Joao D.", since: "Il y a 17 semaines" },
              { name: "Kevin J.", since: "Il y a 17 semaines" },
              { name: "Julia L.", since: "Il y a 17 semaines" },
            ].map((a, i) => (
              <Reveal key={a.name} delay={(i % 3) * 0.06}>
                <div className="card-dark p-7 h-full" data-testid={`avis-${i}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <span className="w-11 h-11 rounded-full bg-[#C4A484] text-[#0A0A0A] flex items-center justify-center font-semibold text-lg">
                      {a.name[0]}
                    </span>
                    <div>
                      <p className="font-medium text-[#F5F5F0]">{a.name}</p>
                      <p className="text-xs text-zinc-600">{a.since}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={15} className="text-[#C4A484] fill-[#C4A484]" />
                    ))}
                  </div>
                  <p className="text-xs tracking-[0.2em] uppercase text-zinc-600">Avis Google vérifié</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-10">
            <a
              href="https://www.google.com/search?q=DMZ+Normandie+ma%C3%A7onnerie+Bernay+avis"
              target="_blank"
              rel="noreferrer"
              className="text-xs tracking-[0.25em] uppercase text-[#C4A484] hover:text-[#F5F5F0] transition-colors"
              data-testid="avis-google-link"
            >
              Voir tous les avis sur Google →
            </a>
          </Reveal>
        </div>
      </section>

      <section id="contact" className="bg-[#0D0D0D] border-t border-white/10 py-20 lg:py-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHead overline="Contact & devis" title="Parlons de votre projet" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <Reveal className="lg:col-span-5">
              <p className="text-zinc-400 font-light leading-relaxed mb-8">
                Décrivez-nous votre projet. Nous vous recontacterons afin d'échanger sur vos besoins
                et déterminer les prochaines étapes.
              </p>
              <div className="space-y-5" data-testid="contact-infos">
                <a href={PHONE_LINK} className="flex items-center gap-4 group" data-testid="contact-phone">
                  <span className="w-11 h-11 bg-[#C4A484] text-[#0A0A0A] rounded-full flex items-center justify-center shrink-0"><Phone size={17} /></span>
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-600">Téléphone — 24h/24</span>
                    <span className="text-base font-medium text-[#F5F5F0] group-hover:text-[#C4A484] transition-colors">{PHONE}</span>
                  </span>
                </a>
                <a href={`mailto:${EMAIL}`} className="flex items-center gap-4 group" data-testid="contact-email">
                  <span className="w-11 h-11 border border-white/15 rounded-full text-[#C4A484] flex items-center justify-center shrink-0"><Mail size={17} /></span>
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-600">E-mail</span>
                    <span className="text-base font-medium text-[#F5F5F0] group-hover:text-[#C4A484] transition-colors">{EMAIL}</span>
                  </span>
                </a>
                <p className="flex items-center gap-4">
                  <span className="w-11 h-11 border border-white/15 rounded-full text-[#C4A484] flex items-center justify-center shrink-0"><Clock size={17} /></span>
                  <span className="text-sm font-light text-zinc-400">Joignable 24 h/24 — 7 j/7, devis gratuit</span>
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
                {["Devis gratuit", "Bernay & Évreux", "Eure et Normandie"].map((x) => (
                  <span key={x} className="flex items-center gap-2 text-sm font-light text-zinc-400">
                    <CheckCircle2 size={15} className="text-[#C4A484]" /> {x}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <DevisForm />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
